import { Workbook } from "exceljs";
import {
	reportsService,
	EnrollmentSummaryResponse,
	IncentiveDisbursementResponse,
	EducationalEngagementResponse,
	EventActivityResponse,
	EventRatingsResponse,
} from "../../services/reports.service";
import { addCoverSheet } from "./cover-sheet";
import { addEnrollmentSummarySheet } from "./enrollment-summary-sheet";
import { addIncentiveDisbursementSheets } from "./incentive-disbursement-sheets";
import { addEducationalEngagementSheets } from "./educational-engagement-sheets";
import { addEventActivitySheet } from "./event-activity-sheet";
import { addEventRatingsSheets } from "./event-ratings-sheets";
import { filenameSlug, isoDateOnly } from "./workbook-styles";

export interface ProgramReportConfig {
	incentiveDisbursement?: boolean;
	educationalEngagement?: boolean;
	eventActivity?: boolean;
	eventRatings?: boolean;
}

export interface BuildWorkbookFilters {
	accountId: string;
	programId: string;
	programName: string;
	reportConfig: ProgramReportConfig;
	// Symbol from the account's `currency` setting (e.g. "$", "£"). Threaded
	// into the workbook so XLSX cells honour the same currency the UI shows.
	// Optional with a "$" fallback for callers that can't synchronously read
	// the setting (SIMS-6940). The Reports page passes the real value.
	currencyMask?: string;
	startDate?: string;
	endDate?: string;
	orgUnitId?: string;
	researcherId?: string;
	eventId?: string;
	enrollmentId?: string;
}

export interface BuiltWorkbook {
	blob: Blob;
	filename: string;
}

// Backend caps pageSize at 100 (@Max(100) on the DTO). Export needs the full
// dataset, so we page through the entire result set client-side. KPIs and
// other aggregates are the same on every page — only the per-enrollment row
// list is concatenated.
const EXPORT_PAGE_SIZE = 100;

const fetchAllPages = async <
	TResp extends { pagination?: { total: number; pageSize: number } },
>(
	fetchPage: (pageNumber: number) => Promise<TResp>,
	rowsKey: keyof TResp,
): Promise<TResp> => {
	const first = await fetchPage(0);
	const pagination = first.pagination;
	if (!pagination || pagination.total <= pagination.pageSize) {
		return first;
	}
	const totalPages = Math.ceil(pagination.total / pagination.pageSize);
	const rest = await Promise.all(
		Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 1)),
	);
	const firstRows = first[rowsKey] as unknown as unknown[];
	const merged = [
		...firstRows,
		...rest.flatMap((p) => p[rowsKey] as unknown as unknown[]),
	];
	return { ...first, [rowsKey]: merged };
};

/**
 * A filter's display name for the cover sheet (SIMS-5825).
 *
 * "All" when nothing is selected. Falls back to the id if the option list does
 * not contain it — an unresolved id is poor, but silently printing "All" would
 * claim the export spans everything when it spans one, which is the failure
 * SIMS-8903 called out on this same sheet.
 */
const optionName = (
	options: Array<{ id: string; name: string }>,
	id: string | undefined,
): string => {
	if (!id) return "All";
	return options.find((o) => o.id === id)?.name ?? id;
};

export const buildWorkbook = async (
	filters: BuildWorkbookFilters,
): Promise<BuiltWorkbook> => {
	const baseFilters = {
		accountId: filters.accountId,
		programId: filters.programId,
		startDate: filters.startDate,
		endDate: filters.endDate,
		orgUnitId: filters.orgUnitId,
		researcherId: filters.researcherId,
	};

	const enrollmentSummaryPromise = fetchAllPages<EnrollmentSummaryResponse>(
		(pageNumber) =>
			reportsService.getEnrollmentSummary({
				...baseFilters,
				pageNumber,
				pageSize: EXPORT_PAGE_SIZE,
			}),
		"enrollments",
	);

	const incentivePromise = filters.reportConfig.incentiveDisbursement
		? fetchAllPages<IncentiveDisbursementResponse>(
				(pageNumber) =>
					reportsService.getIncentiveDisbursement({
						...baseFilters,
						pageNumber,
						pageSize: EXPORT_PAGE_SIZE,
					}),
				"byEnrollment",
			)
		: Promise.resolve(null);

	const educationalPromise = filters.reportConfig.educationalEngagement
		? fetchAllPages<EducationalEngagementResponse>(
				(pageNumber) =>
					reportsService.getEducationalEngagement({
						accountId: filters.accountId,
						programId: filters.programId,
						startDate: filters.startDate,
						endDate: filters.endDate,
						pageNumber,
						pageSize: EXPORT_PAGE_SIZE,
					}),
				"byEnrollment",
			)
		: Promise.resolve(null);

	const eventActivityPromise = filters.reportConfig.eventActivity
		? fetchAllPages<EventActivityResponse>(
				(pageNumber) =>
					reportsService.getEventActivity({
						accountId: filters.accountId,
						programId: filters.programId,
						startDate: filters.startDate,
						endDate: filters.endDate,
						pageNumber,
						pageSize: EXPORT_PAGE_SIZE,
					}),
				"rows",
			)
		: Promise.resolve(null);

	// Event ratings: scoped to the event currently selected in the EventRatings
	// tab. Spec: SOWS → 2 sheets; Mood/Sleep (independent ratings) → 3 sheets.
	// If no event is selected, the backend auto-picks the first rateable event
	// for the program (matches the tab's own auto-pick behavior).
	const eventRatingsPromise = filters.reportConfig.eventRatings
		? (reportsService.getEventRatings({
				accountId: filters.accountId,
				programId: filters.programId,
				eventId: filters.eventId,
				enrollmentId: filters.enrollmentId,
				startDate: filters.startDate,
				endDate: filters.endDate,
			}) as Promise<EventRatingsResponse>)
		: Promise.resolve(null);

	const [
		enrollmentSummary,
		incentive,
		educational,
		eventActivity,
		eventRatings,
	] = await Promise.all([
		enrollmentSummaryPromise,
		incentivePromise,
		educationalPromise,
		eventActivityPromise,
		eventRatingsPromise,
	]);

	const workbook = new Workbook();
	workbook.creator = "SIMS Reporting";
	workbook.created = new Date();

	// SIMS-5825: the cover states the filters by NAME, not by id.
	//
	// Now that the page actually forwards the filters, the legacy
	// `orgUnitFilter` / `researcherFilter` fields would print raw ObjectIds — and
	// an .xlsx travels outside the app, where the reader has no way to resolve
	// one. Uses the `filters` pair list SIMS-8903 added for exactly this, which
	// replaces the legacy rows when supplied.
	//
	// The names come from the enrollment-summary response, which already carries
	// both option lists and is always fetched. The selected value is guaranteed
	// to be in them: org unit options are program-wide, and the researcher
	// options are narrowed by org unit but never by the researcher itself.
	addCoverSheet(workbook, {
		programName: filters.programName,
		exportedAt: new Date(),
		filters: [
			[
				"Start date",
				filters.startDate ? isoDateOnly(filters.startDate) : "All",
			],
			["End date", filters.endDate ? isoDateOnly(filters.endDate) : "All"],
			[
				"Org unit filter",
				optionName(enrollmentSummary.availableOrgUnits, filters.orgUnitId),
			],
			[
				"Researcher filter",
				optionName(
					enrollmentSummary.availableResearchers,
					filters.researcherId,
				),
			],
		],
	});

	addEnrollmentSummarySheet(workbook, enrollmentSummary);
	if (incentive)
		addIncentiveDisbursementSheets(
			workbook,
			incentive,
			filters.currencyMask ?? "$",
		);
	if (educational) addEducationalEngagementSheets(workbook, educational);
	if (eventActivity) addEventActivitySheet(workbook, eventActivity);
	if (eventRatings) addEventRatingsSheets(workbook, eventRatings);

	const arrayBuffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([arrayBuffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});

	const filename = `consolidated_${filenameSlug(filters.programName)}_${isoDateOnly(filters.startDate)}_${isoDateOnly(filters.endDate)}.xlsx`;

	return { blob, filename };
};

export const triggerDownload = ({ blob, filename }: BuiltWorkbook): void => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
};
